import PptxGenJS from 'pptxgenjs';
import PowerPointSlideProcessor from '@app/services/export/ppt/slides/PowerPointSlideProcessor';
import {
	createFullScreenCroppedImageForSlide,
	createImageForSlide,
	createLogoForSlide
} from '@app/services/export/ppt/utils/layoutUtils';
import IThankYouSlideData from '@app/types/data/slides/IThankYouSlideData';
import {
	colors,
	defaultViewPortPadding,
	presentationDimensions,
	spacing
} from '@app/services/export/ppt/variables/constants';
import { gridCol, gridColWidth, gridColX } from '@app/services/export/ppt/utils/gridUtils';
import { copyBold, copySmall, copySmallBold, h3 } from '@app/services/export/ppt/variables/typography';
import qrCode from 'qrcode';
import { getBase64ImageFromSvgElement, getSvgElementFromSvgString } from '@app/services/export/ppt/utils/imageUtils';
import TextProps = PptxGenJS.TextProps;

export default class PowerPointThankYouSlideProcessor extends PowerPointSlideProcessor<IThankYouSlideData> {
	public async processSlideData(): Promise<void> {
		const slide: PptxGenJS.Slide = this.presentation.addSlide();
		const {contacts} = this.slideData;
		const contactTextHeight = contacts.length >= 5 ? 0.93 : 1.2;
		const contactsTotalHeight = contactTextHeight * contacts.length;
		const contactImageDiameter = contacts.length >= 5 ? 0.65 : 0.8;
		const outroTextHeight = 1.1;
		const outroTextStartY = 0.3;
		const outroTextStartX = gridColX(2);
		const firstContactStartY = (presentationDimensions.h - contactsTotalHeight) / 2;
		const firstContactHorizontalCenter = firstContactStartY + contactTextHeight / 2;
		const shapeXStart = gridColX(8);
		const qrDimension = 0.65;
		const outroTextArray: TextProps[] = [];

		// background image
		if (this.slideData.image) {
			createFullScreenCroppedImageForSlide(slide, this.slideData.image);
		}

		// overlay
		slide.addShape('rect', {
			x: shapeXStart,
			y: 0,
			w: gridColWidth(4) + defaultViewPortPadding.horizontal,
			h: presentationDimensions.h,
			fill: {
				color: colors.blue,
				transparency: 20
			}
		});

		// contacts
		contacts.forEach((contact, index) => {
			const textArray: TextProps[] = [];

			if (contact.name) textArray.push({ text: contact.name, options: { ...copyBold, breakLine: true } });
			if (contact.jobTitle) textArray.push({ text: contact.jobTitle, options: { breakLine: true } });
			if (contact.telephone) {
				textArray.push({ text: `${this.i18n.landline}: ${contact.telephone}`, options: { breakLine: true } });
			}
			if (contact.mobile) {
				textArray.push({ text: `${this.i18n.mobile}: ${contact.mobile}`, options: { breakLine: true } });
			}
			if (contact.email) textArray.push({ text: contact.email, options: { breakLine: true } });

			// contact image
			createImageForSlide(
				slide,
				{ ...contact.image, width: 240, height: 240 },
				{
					destinationWidth: contactImageDiameter,
					destinationHeight: contactImageDiameter,
					x: shapeXStart - contactImageDiameter / 2,
					y: firstContactHorizontalCenter - contactImageDiameter / 2 + index * contactTextHeight,
					type: 'cover',
					rounding: true
				}
			);

			// contact text
			slide.addText(textArray, {
				...gridCol(9, 3),
				...copySmall,
				fontSize: contacts.length >= 4 ? 7 : 8,
				color: colors.white,
				y: firstContactHorizontalCenter - contactTextHeight / 2 + index * contactTextHeight,
				h: contactTextHeight,
				margin: [0, 0, 0, 0]
			});
		});

		// outro text
		if (this.slideData.headline) {
			outroTextArray.push({ text: this.slideData.headline, options: { breakLine: true, ...h3 } });
		}

		if (this.slideData.copy) {
			outroTextArray.push({
				text: this.slideData.copy,
				options: { breakLine: true, paraSpaceBefore: spacing.small }
			});
		}

		if (this.slideData.link) {
			outroTextArray.push({
				text: this.slideData.link.label,
				options: { hyperlink: { url: this.slideData.link.url }, color: colors.yellow }
			});
		}

		slide.addText(outroTextArray, {
			...copySmallBold,
			valign: 'top',
			color: colors.white,
			y: outroTextStartY,
			x: outroTextStartX,
			w: gridColWidth(4),
			h: outroTextHeight,
			margin: [0, 0, 0, 0]
		});

		// QR code
		if (this.slideData.link) {

			const svgString =  await qrCode.toString(this.slideData.link.url as string, {type: 'svg', margin: 0});
			const svgElement = getSvgElementFromSvgString(svgString);
			const qrPadding = 0.1;
			const backgroundDimension = qrDimension + qrPadding * 2;

			if (!svgElement) return;

			svgElement.setAttribute('fill', `#${colors.white}`);

			slide.addShape('rect', {
				x: outroTextStartX,
				y: outroTextStartY + outroTextHeight,
				w: backgroundDimension,
				h: backgroundDimension,
				fill: { color: colors.blue }
			});

			slide.addImage({
				data: getBase64ImageFromSvgElement(svgElement),
				x: outroTextStartX + qrPadding,
				y: outroTextStartY + outroTextHeight + qrPadding,
				w: qrDimension,
				h: qrDimension
			});
		}

		createLogoForSlide(slide);
	}
}
